import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useState, useRef } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, API_BASE } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Upload, FileSpreadsheet, RefreshCw, Search, ShieldAlert,
  Mail, Trash2, Sparkles, Database, Loader2,
} from "lucide-react";
import { DataStatusChip } from "@/components/common/DataStatusChip";
import { toast } from "sonner";

export default function DataGovernancePage() {
  const { can, loaded } = usePermissions();
  const [tab, setTab] = useState("mapping");
  const [mapping, setMapping] = useState([]);
  const [summary, setSummary] = useState(null);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [q, setQ] = useState("");
  const [moduleFilter, setModuleFilter] = useState("__all");
  const [aiFilter, setAiFilter] = useState("__all");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, s, dq] = await Promise.all([
        api.get("/data-mapping?limit=1000").catch(() => ({ data: { items: [] } })),
        api.get("/data-mapping/summary").catch(() => ({ data: { total_rows: 0, by_module: [] } })),
        api.get("/data-quality/summary").catch(() => ({ data: null })),
      ]);
      setMapping(m.data.items || []);
      setSummary(s.data);
      setQuality(dq.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (loaded && can("org_mgmt", "view")) load(); /* eslint-disable-next-line */ }, [loaded]);

  const modules = useMemo(() => {
    const set = new Set(mapping.map((r) => r.module).filter(Boolean));
    return Array.from(set).sort();
  }, [mapping]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return mapping.filter((r) => {
      if (moduleFilter !== "__all" && r.module !== moduleFilter) return false;
      if (aiFilter === "ai_only" && !r.ai_derived) return false;
      if (aiFilter === "no_ai" && r.ai_derived) return false;
      if (!ql) return true;
      return `${r.kpi} ${r.data_point} ${r.source_field} ${r.source_system} ${r.calculation}`.toLowerCase().includes(ql);
    });
  }, [mapping, q, moduleFilter, aiFilter]);
  const { sorted: sortedGov, sort: govSort, setSort: setGovSort } = useSortable(filtered);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload the .xlsx template.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/data-mapping/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.rows_imported} mapping rows.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const emailTemplate = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/tools/data-mapping-template/email");
      toast.success(`Emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setEmailing(false); }
  };

  const purge = async () => {
    if (!window.confirm("Purge ALL data-mapping rows? This cannot be undone.")) return;
    try {
      await api.delete("/data-mapping");
      toast.success("Mapping purged");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
  };

  if (!loaded) return <AppLayout title="Data Governance"><div className="h-40 shimmer" /></AppLayout>;
  if (!can("org_mgmt", "view")) {
    return (
      <AppLayout title="Data Governance">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">Admin access required</h2>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Data Governance"
      subtitle="Master data matrix · Data classification · Completeness scoring"
      actions={
        <Button data-testid="dg-refresh-btn" onClick={load} disabled={loading} size="sm" variant="outline">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Mapping rows" value={summary?.total_rows ?? 0} sub={summary?.last_imported_at ? `Imported ${summary.last_imported_at.slice(0, 10)}` : "Not imported yet"} icon={FileSpreadsheet} />
        <KPI label="Modules mapped" value={summary?.by_module?.length ?? 0} sub={summary?.by_module ? `${summary.by_module.reduce((a, b) => a + b.ai_derived_rows, 0)} AI-derived rows` : ""} icon={Database} />
        <KPI label="Overall data completeness" value={quality ? `${quality.grand_completeness_pct}%` : "—"} sub={quality ? `${quality.datasets.length} datasets scored` : ""} icon={Sparkles} tone={quality && quality.grand_completeness_pct > 80 ? "good" : "warn"} />
        <KPI label="Datasets < 60%" value={quality ? quality.datasets.filter((d) => d.overall_pct < 60).length : "—"} sub="Datasets needing attention" icon={ShieldAlert} tone="warn" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger data-testid="dg-tab-mapping" value="mapping" className="text-xs data-[state=active]:bg-white">Master Data Matrix</TabsTrigger>
          <TabsTrigger data-testid="dg-tab-quality" value="quality" className="text-xs data-[state=active]:bg-white">Data Quality</TabsTrigger>
          <TabsTrigger data-testid="dg-tab-classifications" value="classifications" className="text-xs data-[state=active]:bg-white">Classifications</TabsTrigger>
        </TabsList>

        {/* -------- MAPPING TAB -------- */}
        <TabsContent value="mapping" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
            <div className="mr-auto">
              <div className="font-medium text-slate-900">Master Data Requirements Matrix</div>
              <div className="text-xs text-slate-500">Upload the completed template to activate provenance across the app.</div>
            </div>
            <a href={`${API_BASE}/tools/data-mapping-template`} data-testid="dg-download-template">
              <Button variant="outline" size="sm"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Download template</Button>
            </a>
            <Button data-testid="dg-email-template" onClick={emailTemplate} disabled={emailing} variant="outline" size="sm">
              {emailing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Email me the template
            </Button>
            <input
              ref={fileRef} type="file" accept=".xlsx" className="hidden"
              data-testid="dg-upload-input"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button
              data-testid="dg-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-gbe hover:bg-gbe-hover text-white" size="sm"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              Upload completed template
            </Button>
            {summary?.total_rows > 0 && (
              <Button data-testid="dg-purge-btn" onClick={purge} variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Purge
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-[300px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="dg-mapping-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search KPI, field, calc…" className="pl-9 h-9 bg-white border-slate-200" />
            </div>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger data-testid="dg-mapping-module-filter" className="w-[220px] h-9 bg-white"><SelectValue placeholder="Module" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All modules</SelectItem>
                {modules.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={aiFilter} onValueChange={setAiFilter}>
              <SelectTrigger data-testid="dg-mapping-ai-filter" className="w-[180px] h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Any provenance</SelectItem>
                <SelectItem value="ai_only">AI-derived only</SelectItem>
                <SelectItem value="no_ai">Source / calculated only</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-slate-500">Showing {filtered.length} of {mapping.length}</div>
          </div>

          {/* Matrix table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {mapping.length === 0 && !loading ? (
              <div className="p-8 text-center text-sm text-slate-500">
                No mapping rows yet. Upload the completed template above to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <SortableTh label="Module" k="module" sort={govSort} setSort={setGovSort} />
                      <SortableTh label="Tab" k="tab" sort={govSort} setSort={setGovSort} />
                      <SortableTh label="KPI" k="kpi" sort={govSort} setSort={setGovSort} />
                      <SortableTh label="Data Point" k="data_point" sort={govSort} setSort={setGovSort} />
                      <SortableTh label="Source" k="source_system" sort={govSort} setSort={setGovSort} />
                      <SortableTh label="Field" k="source_field" sort={govSort} setSort={setGovSort} />
                      <SortableTh label="Freq" k="update_frequency" sort={govSort} setSort={setGovSort} />
                      <th className="text-left font-medium px-3 py-2">Provenance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedGov.slice(0, 500).map((r) => (
                      <tr key={r.id} data-testid={`dg-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60 align-top">
                        <td className="px-3 py-2 font-medium text-slate-800">{r.module}</td>
                        <td className="px-3 py-2 text-slate-600">{r.tab}</td>
                        <td className="px-3 py-2 text-slate-800">{r.kpi}</td>
                        <td className="px-3 py-2 text-slate-600 max-w-[220px]">{r.data_point}</td>
                        <td className="px-3 py-2 text-slate-700">{r.source_system}</td>
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-600 max-w-[220px] truncate" title={r.source_field}>{r.source_field}</td>
                        <td className="px-3 py-2 text-slate-600">{r.update_frequency}</td>
                        <td className="px-3 py-2">
                          <DataStatusChip
                            status={
                              r.ai_derived ? "ai_generated"
                              : (r.source_system || "").toLowerCase().includes("hub calculated") ? "calculated"
                              : (r.source_system || "").toLowerCase().includes("manager") ? "manager_adjusted"
                              : "source"
                            }
                            size="xs"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* -------- QUALITY TAB -------- */}
        <TabsContent value="quality" className="mt-4">
          <div className="space-y-3">
            {!quality ? (
              <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">Loading…</div>
            ) : quality.datasets.map((ds) => (
              <div key={ds.collection} data-testid={`dq-dataset-${ds.collection}`} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="mr-auto">
                    <div className="font-medium text-slate-900">{ds.label}</div>
                    <div className="text-xs text-slate-500">{ds.record_count.toLocaleString()} records · collection <span className="font-mono">{ds.collection}</span></div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-display font-semibold ${ds.overall_pct >= 80 ? "text-emerald-600" : ds.overall_pct >= 60 ? "text-amber-600" : "text-rose-600"}`}>
                      {ds.overall_pct}%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Overall</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                  {ds.fields.map((f) => (
                    <div key={f.field} className="border border-slate-200 rounded-md p-2.5 bg-slate-50/50">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</div>
                      <div className="mt-1 flex items-baseline gap-1.5">
                        <span className={`text-lg font-semibold ${f.pct >= 80 ? "text-emerald-700" : f.pct >= 60 ? "text-amber-700" : "text-rose-700"}`}>{f.pct}%</span>
                        <span className="text-[10px] text-slate-500">{f.populated}/{f.total}</span>
                      </div>
                      <div className="mt-1 h-1 rounded bg-slate-200 overflow-hidden">
                        <div className={`h-full ${f.pct >= 80 ? "bg-emerald-500" : f.pct >= 60 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${f.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* -------- CLASSIFICATIONS TAB -------- */}
        <TabsContent value="classifications" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
            <div>
              <div className="font-display text-lg font-semibold text-slate-900">Data Classification Reference</div>
              <p className="text-sm text-slate-500 mt-1">Every KPI and data point across GBE Hub is tagged with one of these classifications so the origin of every number is always visible.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <ClassCard chip="source"
                title="Source Data"
                desc="Directly retrieved from an approved source system (AroFlo, Xero, MYOB, SharePoint, HSEQ). Treated as the system of record." />
              <ClassCard chip="calculated"
                title="Hub Calculated"
                desc="Computed by GBE Hub using a defined formula. Examples: GP %, BU-allocated overhead, asset priority score, client health score." />
              <ClassCard chip="ai_generated"
                title="AI Generated / Estimated"
                desc="Created or inferred by an AI model. Should be treated as an estimate. Examples: replacement recommendations, opportunity ranking." />
              <ClassCard chip="manager_adjusted"
                title="Manager Adjusted"
                desc="A calculated or estimated value subsequently approved or changed by an authorised user. All adjustments are audited." />
              <ClassCard chip="ai_pending"
                title="AI Extracted — Pending Verification"
                desc="AI extraction of a value that still needs human sign-off. Used for contract terms, invoice line items, etc." />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}


function KPI({ label, value, sub, icon: Icon, tone = "default" }) {
  const border = tone === "good" ? "border-emerald-200 bg-emerald-50/40"
              : tone === "warn" ? "border-amber-200 bg-amber-50/40"
              :                   "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg p-3 border ${border}`}>
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function ClassCard({ chip, title, desc }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
      <div className="flex items-center gap-2 mb-2">
        <DataStatusChip status={chip} size="md" />
      </div>
      <div className="font-medium text-slate-900">{title}</div>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}
